import { IconButton, Link, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import {
  colorByUsersStatus,
  ENUM_LOCALSTORAGE,
  ENUM_USERS_TYPE,
  popupInfoByType
} from '../../../../enums/enums';
import { IconCircleCheck, IconEdit, IconMail, IconPencil } from '@tabler/icons-react';
import CusItemTooltip from '../../../../components/material-ui/CusItemTooltip';
import TypographyText from '../../../../components/material-ui/table/TypographyText';
import ShowContainer from '../../../../components/material-ui/ShowContainer';
import { getOnlyData } from '../../../../utils';
import dayjs from 'dayjs';
import CusLink from '../../../../components/material-ui/CusLink';
import useColor from '../../../../hooks/site/useColor';
import ChangeCommentModal from '../../modal/ChangeComment';
import ConfirmEmail from '../../modal/ConfirmEmail';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';
import MenuSwitches from './MenuSwitches';
import ChangeLoginModal from '../../modal/ChangeLoginModal';
import ChangeEmailModal from '../../modal/ChangeEmailModal';

export default function AccountInfo({
  selectUserFull,
  setSelectUserFull,
  PinElement = () => <></>
}) {
  const { success, error, primary, divider } = useColor();
  const { t } = useTranslation();

  const [showDopInfo, setShowDopInfo] = useLocalStorage(
    ENUM_LOCALSTORAGE.showDopInfoUserMenu,
    false
  );
  const [openChangeComment, setOpenChangeComment] = useState(false);
  const [openChangeEmail, setOpenChangeEmail] = useState(false);
  const [openConfirmEmail, setOpenConfirmEmail] = useState(false);
  const [openChangeLogin, setOpenChangeLogin] = useState(false);

  const userId = selectUserFull.userId;
  const tgInfo = selectUserFull.telegram;
  const tgNick = tgInfo?.nickname;
  const isGamer = selectUserFull.type == ENUM_USERS_TYPE.gamer;
  const groupName = selectUserFull.group
    ? `${selectUserFull.group.id}: ${selectUserFull.group.name}`
    : '';

  const getDate = (date) => getOnlyData(dayjs(date), '', 'DD.MM.YYYY HH:mm');

  const infoFields = {
    common: [
      {
        id: selectUserFull.phone ? 'phone' : 'login',
        name: 'Login',
        value: selectUserFull.phone ? selectUserFull.phone : selectUserFull.login,
        isEditModal: !selectUserFull.telegram?.id
      },
      { id: 'id', name: 'ID', value: userId },
      { id: 'type', name: 'Type', value: selectUserFull.type },
      { id: 'group', name: 'Group', value: groupName }
    ],
    dopInfo: [
      isGamer && {
        id: 'tg',
        name: 'Nickname in TG',
        value: tgNick && (
          <CusLink
            text={tgNick}
            href={`https://t.me/${tgNick}`}
            target="_blank"
            linkComponent="a"
          />
        )
      },
      isGamer && { id: 'tgId', name: 'Telegram Id', value: tgInfo?.id },
      isGamer && { id: 'tgFirstName', name: 'First name in TG', value: tgInfo?.firstName },
      isGamer && { id: 'tgLastName', name: 'Last name in TG', value: tgInfo?.lastName },
      isGamer && { id: 'tel', name: 'Phone number in TG', value: selectUserFull.phoneNum },
      {
        id: 'register',
        name: 'Register',
        value: selectUserFull.regDate && getDate(selectUserFull.regDate)
      },
      {
        id: 'entered',
        name: 'Entered',
        value: selectUserFull.lastEntry && getDate(selectUserFull.lastEntry)
      },
      {
        id: 'lastActivity',
        name: 'Last activity',
        value: selectUserFull.lastActivity && getDate(selectUserFull.lastActivity)
      },
      isGamer && {
        id: 'authorizations',
        name: 'Number of Inputs',
        value: selectUserFull.entryCount
      },
      {
        id: 'comment',
        name: 'Comment',
        value: selectUserFull.comment,
        isEdit: true,
        onClick: () => setOpenChangeComment(true)
      }
    ].filter((el) => el)
  };

  const MailType = ({ approve }) => {
    if (approve) {
      return (
        <CusItemTooltip text={'Email confirmed'} elem={<IconCircleCheck color={success.main} />} />
      );
    }
    return (
      <CusItemTooltip
        text={'Not confirmed. Confirm?'}
        elem={
          <IconButton onClick={() => setOpenConfirmEmail(true)} sx={{ p: 0 }}>
            <IconMail color={error.main} cursor="pointer" />
          </IconButton>
        }
      />
    );
  };

  const onChangeEmail = () => {
    setOpenChangeEmail(true);
  };

  return (
    <>
      <Stack gap={1.5}>
        <Stack gap={1} pl={1}>
          <ShowContainer condition={PinElement}>
            <Stack
              gap={1}
              flexWrap={'wrap'}
              direction={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}>
              <Typography variant="h5">{t('Account')}</Typography>
              <PinElement />
            </Stack>
          </ShowContainer>

          {infoFields.common.map((field) => {
            const IconUserType = () => {
              if (field.id == 'type') {
                const type = field.value;
                if (!type) return <></>;

                const IconUserType = popupInfoByType[type]?.icon;
                return <IconUserType color={colorByUsersStatus[selectUserFull.type]} size={18} />;
              }

              return <></>;
            };

            return (
              <Stack
                key={field.id}
                direction={'row'}
                gap={1}
                flexWrap={'wrap'}
                alignItems={'center'}>
                <Typography width={'90px'} component={'span'} fontSize={13}>
                  {t(field.name)}:
                </Typography>{' '}
                <Stack direction={'row'} gap={0.5}>
                  <Typography component={'span'} variant="caption" fontWeight={600}>
                    {field.id == 'type' ? (
                      <Stack direction={'row'} gap={0.5} alignItems={'center'}>
                        <IconUserType />
                        <TypographyText sx={{ color: colorByUsersStatus[field.value] }}>
                          {t(field.value)}
                        </TypographyText>
                      </Stack>
                    ) : (
                      field.value || '-'
                    )}
                  </Typography>
                  <ShowContainer condition={field.isEditModal && selectUserFull.userId}>
                    <IconButton
                      sx={{ p: 0.5, ml: 'auto' }}
                      onClick={() => setOpenChangeLogin(true)}>
                      <IconPencil color={userId ? primary.main : divider} />
                    </IconButton>
                  </ShowContainer>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
        <ShowContainer condition={isGamer}>
          <MenuSwitches user={selectUserFull} setSelectUserFull={setSelectUserFull} />
        </ShowContainer>

        <ShowContainer condition={showDopInfo}>
          <Stack gap={1} pl={1}>
            {infoFields.dopInfo.map((field) => (
              <React.Fragment key={field.id}>
                <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
                  <Typography color="textDisabled" fontSize={12} width={'90px'} component={'span'}>
                    {t(field.name)}:
                  </Typography>{' '}
                  <Stack direction={'row'} gap={1} alignItems={'center'}>
                    {field.id == 'tel' && field.value ? (
                      <Stack direction={'row'} gap={0.5} alignItems={'center'}>
                        <TypographyText>{field.value}</TypographyText>
                        <CusItemTooltip
                          text={
                            selectUserFull.phoneConfirmed
                              ? 'Phone confirmed'
                              : 'Phone not confirmed'
                          }
                          elem={
                            <IconCircleCheck
                              color={selectUserFull.phoneConfirmed ? success.main : error.main}
                            />
                          }
                        />
                      </Stack>
                    ) : (
                      <Typography component={'span'} variant="caption" fontWeight={600}>
                        {field.value === 0 ? 0 : field.value || '-'}
                      </Typography>
                    )}

                    <ShowContainer condition={field.isEdit}>
                      <IconButton
                        sx={{ p: 0.5, ml: 'auto' }}
                        onClick={field.onClick}
                        disabled={!userId}>
                        <IconPencil color={userId ? primary.main : divider} />
                      </IconButton>
                    </ShowContainer>
                  </Stack>
                </Stack>
                {field.id == 'tgId' && selectUserFull.emailAddress && (
                  <Stack direction={'row'} gap={1} alignItems={'center'} flexWrap={'wrap'}>
                    <Typography
                      color="textDisabled"
                      fontSize={12}
                      component={'span'}
                      width={'90px'}>
                      Email
                    </Typography>
                    <Stack direction={'row'} gap={1} alignItems={'center'}>
                      <Typography>{selectUserFull.emailAddress}</Typography>
                      <MailType approve={selectUserFull.emailConfirmed} />
                      <IconButton
                        sx={{ p: 0.5, ml: 'auto' }}
                        onClick={onChangeEmail}
                        disabled={!userId}>
                        <IconPencil color={userId ? primary.main : divider} />
                      </IconButton>
                    </Stack>
                  </Stack>
                )}
              </React.Fragment>
            ))}
          </Stack>
        </ShowContainer>
        <Link
          component={'button'}
          onClick={() => setShowDopInfo(!showDopInfo)}
          textAlign={'start'}
          pl={1}>
          {t(showDopInfo ? 'Hide details' : 'More details')}
        </Link>
      </Stack>

      <ChangeCommentModal
        open={openChangeComment}
        closeModal={() => setOpenChangeComment(false)}
        setSelectUserFull={setSelectUserFull}
        user={selectUserFull}
      />

      <ChangeEmailModal
        open={openChangeEmail}
        closeModal={() => setOpenChangeEmail(false)}
        setSelectUserFull={setSelectUserFull}
        user={selectUserFull}
      />

      <ConfirmEmail
        open={openConfirmEmail}
        closeModal={() => setOpenConfirmEmail(false)}
        setSelectUserFull={setSelectUserFull}
        user={selectUserFull}
      />

      <ChangeLoginModal
        open={openChangeLogin}
        closeModal={() => setOpenChangeLogin(false)}
        setSelectUserFull={setSelectUserFull}
        user={selectUserFull}
      />
    </>
  );
}
